<script lang="ts">
	import { goto } from '$app/navigation';
	import { t, type Language } from '$lib/i18n';
	import { captureEvent } from '$lib/analytics/posthog';
	import { getContext } from 'svelte';
	import type { ThemeStore } from '$lib/stores/theme.svelte';
	import { isMobileDevice } from '$lib/utils/motion';

	interface Props {
		lang: Language;
		onClose: () => void;
		onOpenTerminal: () => void;
	}

	let { lang, onClose, onOpenTerminal }: Props = $props();
	let tr = $derived(t(lang));
	const themeStore = getContext<ThemeStore>('theme');

	let query = $state('');
	let selectedIndex = $state(0);
	let inputEl: HTMLInputElement;

	interface Command {
		id: string;
		label: string;
		action: () => void;
		icon: string;
	}

	let allCommands = $derived<Command[]>([
		{
			id: 'nav-services',
			label: tr('palette_nav_services'),
			action: () => goto(`/${lang}/services`),
			icon: 'M13 10V3L4 14h7v7l9-11h-7z'
		},
		{
			id: 'nav-platform',
			label: tr('palette_nav_platform'),
			action: () => goto(`/${lang}/analytics`),
			icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
		},
		{
			id: 'nav-sensors',
			label: tr('palette_nav_sensors'),
			action: () => goto(`/${lang}/sensors`),
			icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'
		},
		{
			id: 'nav-about',
			label: tr('palette_nav_about'),
			action: () => goto(`/${lang}/about`),
			icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'
		},
		{
			id: 'nav-contact',
			label: tr('palette_nav_contact'),
			action: () => goto(`/${lang}/#contact`),
			icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
		},
		{
			id: 'terminal',
			label: tr('palette_open_terminal'),
			action: () => onOpenTerminal(),
			icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
		},
		{
			id: 'language',
			label: tr('palette_switch_language'),
			action: () => {
				const newLang = lang === 'en' ? 'no' : 'en';
				const path = window.location.pathname.replace(/^\/(en|no)/, `/${newLang}`);
				goto(path);
			},
			icon: 'M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129'
		},
		{
			id: 'theme',
			label: tr('palette_toggle_theme'),
			action: () => themeStore.toggle(),
			icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z'
		}
	]);

	// Hide terminal option on mobile for performance
	let commands = $derived(
		isMobileDevice() ? allCommands.filter((c) => c.id !== 'terminal') : allCommands
	);

	let filtered = $derived(
		query.trim()
			? commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
			: commands
	);

	$effect(() => {
		// Reset selection when filtered list changes
		filtered;
		selectedIndex = 0;
	});

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') {
			onClose();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			selectedIndex = (selectedIndex + 1) % filtered.length;
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			selectedIndex = (selectedIndex - 1 + filtered.length) % filtered.length;
		} else if (e.key === 'Enter' && filtered.length > 0) {
			e.preventDefault();
			filtered[selectedIndex].action();
			onClose();
		}
	}

	function executeCommand(cmd: Command) {
		captureEvent('command_palette_executed', { command: cmd.id });
		cmd.action();
		onClose();
	}
</script>

<div
	class="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]"
	onkeydown={handleKeydown}
	role="presentation"
>
	<!-- Backdrop -->
	<button
		class="absolute inset-0 bg-black/60 backdrop-blur-sm"
		onclick={onClose}
		aria-label="Close command palette"
	></button>

	<!-- Palette -->
	<div
		class="relative w-full max-w-lg mx-4 glass-strong rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
		role="dialog"
		aria-label="Command palette"
	>
		<div class="p-4 border-b border-white/[0.08]">
			<input
				bind:this={inputEl}
				bind:value={query}
				type="text"
				placeholder={tr('palette_placeholder')}
				class="w-full bg-transparent text-slate-100 placeholder-slate-500 text-lg outline-none"
				autofocus
			/>
		</div>

		<div class="max-h-80 overflow-y-auto p-2">
			{#if filtered.length === 0}
				<p class="text-slate-500 text-sm text-center py-8">{tr('palette_no_results')}</p>
			{:else}
				{#each filtered as cmd, i}
					<button
						class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors {i ===
						selectedIndex
							? 'bg-white/[0.08] text-white'
							: 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'}"
						onclick={() => executeCommand(cmd)}
						onmouseenter={() => (selectedIndex = i)}
					>
						<svg
							aria-hidden="true"
							class="w-5 h-5 flex-shrink-0"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="1.5"
								d={cmd.icon}
							/>
						</svg>
						<span class="text-sm font-medium">{cmd.label}</span>
					</button>
				{/each}
			{/if}
		</div>

		<div
			class="px-4 py-3 border-t border-white/[0.08] flex items-center gap-4 text-xs text-slate-500"
		>
			<span
				><kbd class="px-1.5 py-0.5 bg-white/[0.06] rounded text-slate-400">↑↓</kbd> navigate</span
			>
			<span><kbd class="px-1.5 py-0.5 bg-white/[0.06] rounded text-slate-400">↵</kbd> select</span>
			<span><kbd class="px-1.5 py-0.5 bg-white/[0.06] rounded text-slate-400">esc</kbd> close</span>
		</div>
	</div>
</div>
